<template>
  <div class="qb-outputs">
    <label class="text-lg text-orange">{{$t('outputs')}}</label>
    <div id="outputs" class="cell-select card">
        <span v-for="(output, i) in outputsData" :key="i"
              :class="{cell: true, checked: queryOutputs.indexOf(output.output_id) !== -1, disabled: isOutputDisabled(output.output_id)}"
        >
          <label class="d-flex align-items-center">{{output.name}} 
            <span @click.prevent.self="() => setDefaultOutput(output.output_id)"
                v-show="queryOutputs.indexOf(output.output_id) !== -1 && queryOutputs.length > 1"
                :class="{far: output.output_id !== defaultOutput, fas: output.output_id === defaultOutput, 'icon-grey': output.output_id !== defaultOutput, 'icon-orange': output.output_id === defaultOutput}"
                class="fa-star pl5 link-pointer"
            ></span>
            <span v-if="output.tooltip"
                class="fas fa-info-circle pl5 link-pointer icon-grey"
                v-tooltip="output.tooltip"
            ></span>
            <span v-if="queryOutputs.indexOf(output.output_id) !== -1 && output.settings"
                class="fas fa-cog pl5 link-pointer icon-grey"
                @click="() => toggleShowOutputSettingsModal(true, output.output_id)"
            ></span>
          </label>
          
          <span :class="['cell-toggle', runIsBusy ? 'disabled' : '']" @click="() => toggleOutput(output)"></span>
        </span>
    </div>

    <FormModal
      v-if="showOutputSettingsModal"
      :loading="outputSettingsLoading"
      id="output-settings-form"
      title="output.settings_header"
      :subtitle="activeOutput.name"
      :model="model"
      :fieldsObjects="outputSettingsForm"
      @closeModal="() => toggleShowOutputSettingsModal(false)"
      @onSave="saveSettings"
      @updateModel="updateModel"
    />
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import _ from 'lodash';
  import { QUERIES, VALIDATIONS } from '@/config/const';
  import FormModal from '@/components/form-modal';
  import FormHelper from '@/helpers/form';

  export default {
    props: {
      outputList: {
        type: Array,
        required: true
      },
      defaultOutput: {
        type: Number
      },
      queryOutputs: {
        type: Array,
        default: () => []
      },
      queryOutputSettings: {
        type: Array,
        default: () => []
      },
      queryRows : {
       type: Array,
       default: () => [] 
      }
    },

    computed: {
      ...mapState({
        runIsBusy: state => state.queryBuilder.query.run.isBusy,
      }),
      outputsData () {
        return this.outputList.map(output => {
          const data = { ...output }

          data.tooltip = this.$te('output.tooltips.' + data.output_id) && this.$t('output.tooltips.' + data.output_id);

          let outputSettings;
          if (data.output_id === QUERIES.OUTPUTS.DISTINCT_COUNT) {
            outputSettings = {
              type: {
                id: 'type',
                name: 'type',
                label: this.$t('output.fields.type'),
                type: 'select',
                trackBy: 'value',
                validate: [VALIDATIONS.REQUIRED],
                value: null,
                allowEmpty: false,
                searchable: true,
                preselectFirst: true,
                options: [
                  {
                    label: 'Category',
                    value: 'category'
                  },
                  {
                    label: 'Standard Breakdown',
                    value: 'breakdown'
                  }
                ],
                style: 'flex-basis: 100%',
              }
            }

            const savedSettings = this.queryOutputSettings.find(output => output.output_id === QUERIES.OUTPUTS.DISTINCT_COUNT);
            const selectedType = this.model.type || savedSettings && savedSettings.settings.type;

            if (selectedType === 'category') {
              outputSettings.category_id = {
                id: 'category_id',
                name: 'category_id',
                label: this.$t('output.fields.category_id'),
                type: 'select',
                trackBy: 'value',
                validate: [VALIDATIONS.REQUIRED],
                value: null,
                allowEmpty: false,
                searchable: true,
                options: (this.categoriesData || []).map(dt => ({ value: dt.id, label: dt.name }))
              }
            } else if (selectedType === 'breakdown') {
              outputSettings.breakdown_id = {
                id: 'breakdown_id',
                name: 'breakdown_id',
                label: this.$t('output.fields.breakdown_id'),
                type: 'select',
                trackBy: 'value',
                validate: [VALIDATIONS.REQUIRED],
                value: null,
                allowEmpty: false,
                searchable: true,
                options: (this.breakdownsData || []).map(dt => ({ value: dt.id, label: dt.name }))
              }
            }
          } else if (data.output_id === QUERIES.OUTPUTS.PERCENTAGE) {
            
            const savedSettings = this.queryOutputSettings.find(output => output.output_id === QUERIES.OUTPUTS.PERCENTAGE);
            const selectedCategoryId = this.model.category_id || savedSettings && savedSettings.settings.category_id;

            const tagOptions = (this.tagsData || []).map(dt => ({ value: dt.id, label: dt.name }));

            if (selectedCategoryId && tagOptions.length > 0) {
              tagOptions.unshift({
                category_id: selectedCategoryId,
                label: this.$t('category_tags.any_tag'),
                value: 'all'
              });
            }

            outputSettings = {
              category_id: {
                id: 'category_id',
                name: 'category_id',
                label: this.$t('output.fields.category_id'),
                type: 'select',
                trackBy: 'value',
                validate: [VALIDATIONS.REQUIRED],
                value: null,
                allowEmpty: false,
                searchable: true,
                preselectFirst: true,
                options: (this.categoriesData || []).map(dt => ({ value: dt.id, label: dt.name })),
                style: 'flex-basis: 100%',
              },
              tag_id: {
                id: 'tag_id',
                name: 'tag_id',
                label: this.$t('output.fields.tag_id'),
                placeholder: this.$t('output.placeholders.percentage_tag_id'),
                type: 'select',
                trackBy: 'value',
                validate: [VALIDATIONS.REQUIRED],
                value: null,
                allowEmpty: false,
                searchable: true,
                internalSearch: false,
                options: tagOptions,
                style: 'flex-basis: 100%',
                loading: !this.tagsData,
                onSearchChange: (text) => this.searchTags(selectedCategoryId, text)
              }
            }

            
          } else if (data.output_id === QUERIES.OUTPUTS.ARTICLE_COUNT) {
            const savedSettings = this.queryOutputSettings.find(output => output.output_id === QUERIES.OUTPUTS.ARTICLE_COUNT);
            const label = savedSettings ? savedSettings.settings.label : '';
            outputSettings = {
              label: {
                id: 'label',
                name: 'label',
                label: this.$t('output.fields.article_count_label'),
                type: 'input',
                value: label,
                placeholder: this.$t('output.placeholders.article_count_label'),
              }
            }
          } else if (data.output_id === QUERIES.OUTPUTS.TREND_PERCENTAGE) {
            const savedSettings = this.queryOutputSettings.find(output => output.output_id === QUERIES.OUTPUTS.TREND_PERCENTAGE);
            const label = savedSettings ? savedSettings.settings.label : '';
            outputSettings = {
              label: {
                id: 'label',
                name: 'label',
                label: this.$t('output.fields.article_count_label'),
                type: 'input',
                value: label,
                placeholder: this.$t('output.placeholders.trends_label'),
              }
            }
          } else if (data.output_id === QUERIES.OUTPUTS.MENTIONS) {
            const savedSettings = this.queryOutputSettings.find(output => output.output_id === QUERIES.OUTPUTS.MENTIONS);
            const label = savedSettings ? savedSettings.settings.label : '';
            outputSettings = {
              label: {
                id: 'label',
                name: 'label',
                label: this.$t('output.fields.mentions_label'),
                type: 'input',
                value: label,
                placeholder: this.$t('output.placeholders.mentions_label'),
              }
            }
          } else if (data.output_id === QUERIES.OUTPUTS.RANK) {
            const savedSettings = this.queryOutputSettings.find(output => output.output_id === QUERIES.OUTPUTS.RANK);
            const rankByMostRecentPeriod = savedSettings ? savedSettings.settings.rank_by_most_recent_period : false;
            const rankByAggregatedValue = savedSettings ? !savedSettings.settings.rank_by_aggregated_value && !rankByMostRecentPeriod ? true : savedSettings.settings.rank_by_aggregated_value : true;
            outputSettings = {
              rank_by_most_recent_period: {
                id: 'rank_by_most_recent_period',
                name: 'rank_by_most_recent_period',
                label: this.$t('output.fields.rank_by_most_recent_period'),
                type: 'toggle',
                value: rankByMostRecentPeriod,
                style: 'flex-basis: 100%',
              },
              rank_by_aggregated_value: {
                id: 'rank_by_aggregated_value',
                name: 'rank_by_aggregated_value',
                label: this.$t('output.fields.rank_by_aggregated_value'),
                type: 'toggle',
                value: rankByAggregatedValue,
                style: 'flex-basis: 100%',
              }
            }
          }

          if (outputSettings) {
            data.settings = outputSettings;
          }

          return data;
        })
      },

      outputSettingsForm () {
        return this.activeOutput && this.getOutputSettingsForm(this.activeOutput) || {};
      },

      activeOutput () {
        return this.activeOutputId && this.outputsData.find(output => output.output_id === this.activeOutputId);
      },

      projectId () {
        return this.$route.params.project_id;
      },

      outputSettingsLoading () {
        if (this.activeOutputId === QUERIES.OUTPUTS.DISTINCT_COUNT) {
          return !this.categoriesData || !this.breakdownsData;
        }
        if (this.activeOutputId === QUERIES.OUTPUTS.PERCENTAGE) {
          return !this.categoriesData;
        }
        return false;
      }
    },

    components: {
      FormModal
    },

    data () {
      return {
        showOutputSettingsModal: false,
        validateOutputSettings: false,
        activeOutputId: null,
        model: {},

        // Data used in outputs settings
        categoriesData: null,
        breakdownsData: null,
        tagsData: null
      }
    },

    methods: {
      ...mapActions({
        getCategories: 'queryBuilder/settings/getCategories',
        getBreakdowns: 'queryBuilder/settings/getBreakdowns',
        getTags: 'queryBuilder/settings/getTags',
      }),

      makeModel () {
        const savedSettings = this.queryOutputSettings.find(output => output.output_id === this.activeOutputId)?.settings || {};
        const keys = Object.keys(this.activeOutput.settings);
        this.model = keys.reduce((acc, key) => ({ ...acc, [key]: savedSettings[key] }), {});
      },

      updateModel (model, field) {
        if (this.activeOutputId === QUERIES.OUTPUTS.PERCENTAGE && field && field.id === 'category_id') {
          model.tag_id = null;
        }
        if (this.activeOutputId === QUERIES.OUTPUTS.DISTINCT_COUNT && field && field.id === 'type') {
          model.category_id = null;
          model.breakdown_id = null;
        }
        if (this.activeOutputId === QUERIES.OUTPUTS.RANK) {
          if (field && field.id === 'rank_by_most_recent_period') {
            model.rank_by_aggregated_value = !model.rank_by_most_recent_period;
          }
          if (field && field.id === 'rank_by_aggregated_value') {
            model.rank_by_most_recent_period = !model.rank_by_aggregated_value;
          }
        }
        this.model = model;
      },

      toggleOutput (output) {

        let newValue = _.cloneDeep(this.queryOutputs);
        const valueIndex = newValue.indexOf(output.output_id);

        if(valueIndex !== -1) {

          //Do not allow to untoggle last selected output
          if(this.queryOutputs.length <= 1)
            return;

          newValue.splice(valueIndex, 1);

          //If defaultOutput has been untoggled, set the next available output as default
          if(this.defaultOutput === output.output_id || this.queryOutputs.length === 1 || !this.defaultOutput)
            this.setDefaultOutput(newValue.sort()[0]);

        }
        else {
          if (output.settings) {
            // When enabling an output, its default settings might not be good to immediately run the query
            // So we need to check that and prompt the output settings modal if needed
            // const settingsModel = this.queryOutputSettings.find(s => s.output_id === output.output_id).settings;
            const isValid = this.checkOutputSettingsAreValid(output);
            if (!isValid) {
              this.toggleShowOutputSettingsModal(true, output.output_id);
              return;
            }
          }

          newValue.push(output.output_id);
        }

        this.$emit('setQueryOutputs', { entity: 'outputs', value: newValue });
      },

      setDefaultOutput (output) {
        this.$emit('setQueryOutputs', { entity: 'default_output', value: output });
      },

      checkOutputSettingsAreValid (output) {
        const form = this.getOutputSettingsForm(output);
        const errors = FormHelper.validateForm(form);
        return errors.length === 0;
      },

      getOutputSettingsForm (output) {
        const form = _.cloneDeep(output.settings);
        const model = this.queryOutputSettings.find(s => s.output_id === output.output_id);
        if (model) {
          const keys = Object.keys(form);
          for (let i = 0; i < keys.length; i++) {
            const field = keys[i];
            form[field].value = model.settings[field];
          }
        }

        return form;
      },

      toggleShowOutputSettingsModal (show, outputId) {
        if (!show) {
          this.showOutputSettingsModal = false;
          this.activeOutputId = null;
          return;
        }

        this.activeOutputId = outputId;
        this.showOutputSettingsModal = true;
      },

      saveSettings (settingsModel) {
        const settings = _.cloneDeep(this.queryOutputSettings);
        const outputId = this.activeOutputId;
        const selectedOutputSettings = settings.find(s => s.output_id === outputId);
        if (selectedOutputSettings) {
          selectedOutputSettings.settings = settingsModel;
        } else {
          settings.push({
            output_id: outputId,
            settings: settingsModel
          });
        }
        
        this.$emit('setQueryOutputs', { entity: 'output_settings', value: settings });
        if (this.queryOutputs.indexOf(outputId) === -1) {
          this.toggleOutput({ output_id: outputId });
        }
        this.toggleShowOutputSettingsModal(false);
      },

      onValidateOutputSettings (invalidFields) {
        if (invalidFields.length === 0) {
          this.toggleShowOutputSettingsModal(false);
        }
      },

      isOutputDisabled (output_id) {
        let disabled = false;
        const mmiProjectId = 23412;
        if (output_id === QUERIES.OUTPUTS.MENTIONS) {
          disabled = this.queryRows.filter(r => r.type === 'tag' || r.type === 'category' || (this.projectId == mmiProjectId && ['outlet', 'byline', 'outlet-media-type'].includes(r.value))).length === 0;
        }
        return disabled;
      },

      searchTags (categoryId, text) {
        this.tagsData = null;
        this.getTags({ projectId: this.projectId, categoryId, query: text, include: this.model.tag_id }).then(r => {
          this.tagsData = r;
        });
      }
    },

    watch: {
      activeOutputId (value) {
        if (value) {
          this.makeModel();

          if (value === QUERIES.OUTPUTS.DISTINCT_COUNT) {
            if (!this.categoriesData) {
              this.getCategories({ projectId: this.projectId }).then(r => {
                this.categoriesData = r;
              });
            }

            if (!this.breakdownsData) {
              this.getBreakdowns({ projectId: this.projectId }).then(r => {
                this.breakdownsData = r;
              });
            }

          } else if (value === QUERIES.OUTPUTS.PERCENTAGE) {
            if (!this.categoriesData) {
              this.getCategories({ projectId: this.projectId }).then(r => {
                this.categoriesData = r;
              });
            }
          }
        } else {
          this.model = {};
        }
      },

      'model.category_id' (value) {
        if (value && this.activeOutputId === QUERIES.OUTPUTS.PERCENTAGE) {
          this.searchTags(value);
        }
      }
    }
  }
</script>